/**
 * Sidebar — composition shell.
 *
 * Phase E1 split: data (projects + direct chats + event bus) lives in
 * `sidebar/useSidebarData`, search in `useSidebarSearch`, user profile
 * in `useSidebarProfile`. Presentational sub-components live under
 * `sidebar/`: NavGroups, AssistantSubmenu, SidebarFooter, SearchModal,
 * ContextMenu, ConfirmDeleteModals. This file only owns the shared
 * UI state (expanded menus, tooltip, rename drafts, context-menu
 * target) and the top-level chrome (brand, tooltip, edge hover zone,
 * create/edit project modals).
 */

import { useEffect, useMemo, useState, type UIEventHandler } from 'react';
import { PanelLeft } from 'lucide-react';
import { emit as emitAppEvent } from '../events/bus';
import ProjectEditModal from '../../modules/chat/ProjectEditModal';
import UserMenu from '../../modules/user/UserMenu/UserMenu';
import type { TabType } from '../../App';
import { type BackendUserWithPlan } from '../../userService';
import NavGroups from './sidebar/NavGroups';
import AssistantSubmenu from './sidebar/AssistantSubmenu';
import SidebarFooter from './sidebar/SidebarFooter';
import SearchModal from './sidebar/SearchModal';
import ContextMenu from './sidebar/ContextMenu';
import ConfirmDeleteModals from './sidebar/ConfirmDeleteModals';
import { useSidebarData } from './sidebar/useSidebarData';
import { useSidebarProfile } from './sidebar/useSidebarProfile';
import { useSidebarSearch } from './sidebar/useSidebarSearch';
import type { ContextMenuTarget, Project } from './sidebar/types';
import './Sidebar.css';
import logo from '../../assets/images/logo.png';
import logoText from '../../assets/images/logo-text.png';

type SidebarProps = {
    activeTab: TabType;
    onTabChange: (tab: TabType, subTab?: string) => void;
    isOpen: boolean;
    collapsed: boolean;
    onToggleCollapse: () => void;
    educationSubTab?: string | undefined;
    networkingSubTab?: string | undefined;
    userData: BackendUserWithPlan | null;
    onChatSelect?: ((projectId: string, chatId: string) => void) | undefined;
    onNewDirectChat?: (() => void) | undefined;
    selectedChatId?: string | undefined;
    selectedProjectId?: string | undefined;
    mobileOpen?: boolean | undefined;
    onMobileClose?: (() => void) | undefined;
};

const ROLE_LABELS: Record<string, string> = {
    admin: 'Admin', mentor: 'Mentor', mentee: 'Mentee',
};

export default function Sidebar({
    activeTab, onTabChange, isOpen, collapsed, onToggleCollapse,
    networkingSubTab = 'connections', userData,
    onChatSelect, onNewDirectChat, selectedChatId, selectedProjectId, mobileOpen,
}: SidebarProps) {
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['assistant']));
    const [tooltip, setTooltip] = useState<{ top: number; left: number } | null>(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    // Track the menu anchor as state (not a ref) so the UserMenu
    // re-renders + repositions once the DOM node attaches. Reading a
    // ref's `.current` during render is flagged by React 19's rules.
    const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLButtonElement | null>(null);

    const [contextMenu, setContextMenu] = useState<ContextMenuTarget | null>(null);
    const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const [renameProjectValue, setRenameProjectValue] = useState('');
    const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
    const [isYourChatsExpanded, setIsYourChatsExpanded] = useState(true);

    const [confirmDeleteChatId, setConfirmDeleteChatId] = useState<string | null>(null);
    const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<string | null>(null);
    const [confirmDeleteProjectChatId, setConfirmDeleteProjectChatId] =
        useState<{ projectId: string; chatId: string } | null>(null);

    const data = useSidebarData({ selectedChatId, onNewDirectChat });
    const profile = useSidebarProfile();
    const search = useSidebarSearch();

    // ── Derived UI state ──────────────────────────────────────────────

    const displayName = userData
        ? (userData.first_name || userData.last_name
            ? `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim()
            : userData.username)
        : (profile.name || 'Loading...');

    const initials = userData
        ? (userData.first_name && userData.last_name
            ? `${userData.first_name[0]}${userData.last_name[0]}`.toUpperCase()
            : userData.username.substring(0, 2).toUpperCase())
        : (profile.initials || '??');

    const mentorshipRole = userData?.profile?.role;
    const backgroundFallback = userData?.profile?.background
        ? (userData.profile.background.length > 25
            ? userData.profile.background.substring(0, 22) + '...'
            : userData.profile.background)
        : null;
    const userRole = (mentorshipRole && ROLE_LABELS[mentorshipRole])
        || backgroundFallback
        || profile.role
        || 'Chiefy Member';

    // ── Context menu outside-click close ──────────────────────────────

    useEffect(() => {
        if (!contextMenu) return;
        const close = (e: Event) => {
            if (e instanceof MouseEvent) {
                const target = e.target as HTMLElement;
                if (target?.closest('.context-menu') || target?.closest('.chat-more-btn')) return;
            }
            setContextMenu(null);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('scroll', close, true);
        };
    }, [contextMenu]);

    // ── Helpers ──────────────────────────────────────────────────────

    const closeSidebarOnMobile = () => {
        if (window.innerWidth <= 768 && !collapsed) onToggleCollapse();
    };

    const handleMoreBtnClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        type: ContextMenuTarget['type'], id: string, projectId?: string,
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuWidth = 160;
        const menuHeight = 84;
        const x = rect.right - menuWidth;
        const y = rect.bottom + 4 + menuHeight > window.innerHeight
            ? rect.top - menuHeight - 4
            : rect.bottom + 4;
        setContextMenu({ x, y, type, id, projectId });
    };

    const handleContextMenuDelete = () => {
        if (!contextMenu) return;
        setContextMenu(null);
        if (contextMenu.type === 'project') {
            setConfirmDeleteProjectId(contextMenu.id);
        } else if (contextMenu.type === 'direct-chat') {
            setConfirmDeleteChatId(contextMenu.id);
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            setConfirmDeleteProjectChatId({
                projectId: contextMenu.projectId,
                chatId: contextMenu.id,
            });
        }
    };

    const handleContextMenuRename = () => {
        if (!contextMenu) return;
        if (contextMenu.type === 'project') {
            const currentName = data.projects.find((p) => p.id === contextMenu.id)?.name || '';
            setContextMenu(null);
            setRenamingProjectId(contextMenu.id);
            setRenameProjectValue(currentName);
            return;
        }
        const chatId = contextMenu.id;
        let currentName = '';
        if (contextMenu.type === 'direct-chat') {
            currentName = data.directChats.find((c) => c.id === chatId)?.name || '';
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            const proj = data.projects.find((p) => p.id === contextMenu.projectId);
            currentName = proj?.chats.find((c) => c.id === chatId)?.name || '';
        }
        setContextMenu(null);
        setRenamingChatId(chatId);
        setRenameValue(currentName);
    };

    const executeRenameProject = async (projectId: string) => {
        await data.renameProjectById(projectId, renameProjectValue);
        setRenamingProjectId(null);
    };

    const executeRenameChat = async (chatId: string, projectId?: string) => {
        await data.renameChatById(chatId, renameValue, projectId);
        setRenamingChatId(null);
    };

    const handleCreateChat = (projectId: string) => {
        onTabChange('assistant');
        onChatSelect?.(projectId, '');
    };

    const handleMainScroll: UIEventHandler<HTMLDivElement> = (e) => {
        if (!isYourChatsExpanded) return;
        if (!data.directChatsMoreUrl || data.isLoadingMoreDirectChats) return;
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (distanceFromBottom < 160) void data.loadMoreDirectChats();
    };

    const showTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTooltip({ top: rect.top + rect.height / 2, left: rect.right + 12 });
    };
    const hideTooltip = () => setTooltip(null);
    const handleToggle = () => { hideTooltip(); onToggleCollapse(); };

    const toggleAssistantMenu = () => {
        setExpandedMenus((prev) => {
            const next = new Set(prev);
            if (next.has('assistant')) next.delete('assistant');
            else next.add('assistant');
            return next;
        });
    };

    // ── Shared derived lists for search modal ────────────────────────

    const sq = search.searchQuery.toLowerCase().trim();
    const filteredProjects = useMemo<Project[]>(() => (
        sq
            ? data.projects.filter((p) =>
                p.name.toLowerCase().includes(sq)
                || p.chats.some((c) => c.name?.toLowerCase().includes(sq)))
            : data.projects
    ), [data.projects, sq]);

    const allRecentChats = useMemo(() => (
        [
            ...data.directChats.map((c) => ({
                id: c.id, name: c.name, updatedAt: '',
                projectId: '', projectName: '',
            })),
            ...data.projects.flatMap((p) => p.chats.map((c) => ({
                id: c.id, name: c.name, updatedAt: c.updatedAt || '',
                projectId: p.id, projectName: p.name,
            }))),
        ]
            .filter((c) => !sq
                || c.name?.toLowerCase().includes(sq)
                || c.projectName?.toLowerCase().includes(sq))
            .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
    ), [data.directChats, data.projects, sq]);

    const assistantSubmenu = (
        <AssistantSubmenu
            selectedChatId={selectedChatId}
            selectedProjectId={selectedProjectId}
            projects={data.projects}
            directChats={data.directChats}
            searchQuery={search.searchQuery}
            isProjectsExpanded={isProjectsExpanded}
            isYourChatsExpanded={isYourChatsExpanded}
            expandedProjects={data.expandedProjects}
            animatingChatIds={data.animatingChatIds}
            renamingProjectId={renamingProjectId}
            renameProjectValue={renameProjectValue}
            renamingChatId={renamingChatId}
            renameValue={renameValue}
            isLoadingMoreDirectChats={data.isLoadingMoreDirectChats}
            onNewDirectChat={onNewDirectChat}
            onChatSelect={onChatSelect}
            onOpenSearch={() => search.setSearchOpen(true)}
            onToggleProjects={() => setIsProjectsExpanded((v) => !v)}
            onToggleRecentChats={() => setIsYourChatsExpanded((v) => !v)}
            onToggleProject={data.toggleProject}
            onAddProject={() => data.setShowCreateProjectModal(true)}
            onMoreClick={handleMoreBtnClick}
            onCreateProjectChat={handleCreateChat}
            onRenameProjectChange={setRenameProjectValue}
            onCommitRenameProject={executeRenameProject}
            onCancelRenameProject={() => setRenamingProjectId(null)}
            onRenameChatChange={setRenameValue}
            onCommitRenameChat={executeRenameChat}
            onCancelRenameChat={() => setRenamingChatId(null)}
            onAnimationDone={data.clearAnimation}
            onScroll={handleMainScroll}
            closeSidebarOnMobile={closeSidebarOnMobile}
        />
    );

    return (
        <>
            <aside className={`sidebar ${isOpen ? 'open' : 'closed'} ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <div className="brand">
                        <div
                            className="brand-logo"
                            onMouseEnter={collapsed ? showTooltip : undefined}
                            onMouseLeave={collapsed ? hideTooltip : undefined}
                        >
                            <img src={logo} alt="Chiefy Logo" className="logo-image" />
                            {collapsed && (
                                <button
                                    type="button"
                                    className="sidebar-toggle-overlay"
                                    onClick={handleToggle}
                                    aria-label="Open sidebar"
                                    title="Open sidebar"
                                >
                                    <PanelLeft size={18} />
                                </button>
                            )}
                        </div>
                        {!collapsed && (
                            <div className="brand-text">
                                <img src={logoText} alt="Chiefy" className="logo-text-image" />
                            </div>
                        )}
                    </div>
                    {!collapsed && (
                        <button
                            type="button"
                            className="icon-btn sidebar-toggle"
                            onClick={handleToggle}
                            title="Close sidebar"
                        >
                            <PanelLeft size={18} />
                        </button>
                    )}
                </div>

                <NavGroups
                    activeTab={activeTab}
                    collapsed={collapsed}
                    userData={userData}
                    networkingSubTab={networkingSubTab}
                    assistantExpanded={expandedMenus.has('assistant')}
                    onTabChange={onTabChange}
                    onToggleCollapse={onToggleCollapse}
                    onToggleAssistantMenu={toggleAssistantMenu}
                    onCollapseAllMenus={() => setExpandedMenus(new Set())}
                    onCloseSidebarOnMobile={closeSidebarOnMobile}
                    assistantSubmenu={assistantSubmenu}
                />

                <SidebarFooter
                    ref={setUserMenuAnchor}
                    userData={userData}
                    displayName={displayName}
                    initials={initials}
                    userRole={userRole}
                    activeTab={activeTab}
                    collapsed={collapsed}
                    userMenuOpen={userMenuOpen}
                    onProfileTab={() => { onTabChange('profile'); closeSidebarOnMobile(); }}
                    onOpenMenu={() => setUserMenuOpen((v) => !v)}
                    onUpgrade={() => {
                        onTabChange('profile');
                        closeSidebarOnMobile();
                        setTimeout(() => emitAppEvent('open-plans', { reason: 'menu' }), 100);
                    }}
                />

                <UserMenu
                    anchor={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={() => setUserMenuOpen(false)}
                    user={userData ?? null}
                    onProfile={() => { onTabChange('profile'); closeSidebarOnMobile(); }}
                    onAccountSettings={() => { onTabChange('settings'); closeSidebarOnMobile(); }}
                    onBilling={() => {
                        onTabChange('settings');
                        if (typeof window !== 'undefined') {
                            window.location.hash = '#billing';
                        }
                        closeSidebarOnMobile();
                    }}
                />
            </aside>

            {tooltip && (
                <div
                    className="sidebar-icon-tooltip"
                    style={{ top: tooltip.top, left: tooltip.left }}
                >
                    {collapsed ? 'Open sidebar' : 'Close sidebar'}
                </div>
            )}

            <div
                className={`sidebar-edge-zone ${collapsed ? 'sidebar-edge-zone--collapsed' : ''}`}
                onClick={handleToggle}
            />

            {contextMenu && (
                <ContextMenu
                    target={contextMenu}
                    onEditProject={() => {
                        setEditingProjectId(contextMenu.id);
                        setContextMenu(null);
                    }}
                    onRename={handleContextMenuRename}
                    onDelete={handleContextMenuDelete}
                />
            )}

            <ConfirmDeleteModals
                directChats={data.directChats}
                projects={data.projects}
                confirmDeleteChatId={confirmDeleteChatId}
                confirmDeleteProjectId={confirmDeleteProjectId}
                confirmDeleteProjectChatId={confirmDeleteProjectChatId}
                isDeletingChatId={data.isDeletingChatId}
                isDeletingProjectId={data.isDeletingProjectId}
                isDeletingProjectChatId={data.isDeletingProjectChatId}
                onCancelChat={() => setConfirmDeleteChatId(null)}
                onCancelProject={() => setConfirmDeleteProjectId(null)}
                onCancelProjectChat={() => setConfirmDeleteProjectChatId(null)}
                onConfirmChat={async (id) => {
                    await data.deleteDirectChat(id);
                    setConfirmDeleteChatId(null);
                }}
                onConfirmProject={async (id) => {
                    await data.deleteProject(id);
                    setConfirmDeleteProjectId(null);
                }}
                onConfirmProjectChat={async (pid, cid) => {
                    await data.deleteProjectChat(pid, cid);
                    setConfirmDeleteProjectChatId(null);
                }}
            />

            <SearchModal
                open={search.searchOpen}
                searchQuery={search.searchQuery}
                onSearchChange={search.handleSearchChange}
                onClose={search.close}
                apiResults={search.apiSearchResults}
                isSearching={search.isSearching}
                onLoadMore={search.loadMore}
                projects={data.projects}
                filteredProjects={filteredProjects}
                allRecentChats={allRecentChats}
                onOpenProject={(projectId) => {
                    onTabChange('assistant');
                    data.toggleProject(projectId);
                }}
                onSelectChat={(projectId, chatId) => {
                    onChatSelect?.(projectId, chatId);
                    closeSidebarOnMobile();
                }}
            />

            {data.showCreateProjectModal && (
                <ProjectEditModal
                    mode="create"
                    onClose={() => data.setShowCreateProjectModal(false)}
                    onSaved={(created) => {
                        const projectWithChats: Project = {
                            id: created.id,
                            name: created.name,
                            description: created.description,
                            files: created.files,
                            chats: [],
                        };
                        data.setProjects((prev) => [projectWithChats, ...prev]);
                        data.expandProject(created.id);
                    }}
                />
            )}

            {editingProjectId && (() => {
                const proj = data.projects.find((p) => p.id === editingProjectId);
                if (!proj) return null;
                return (
                    <ProjectEditModal
                        mode="edit"
                        projectId={proj.id}
                        name={proj.name}
                        description={proj.description || ''}
                        files={proj.files || []}
                        onClose={() => setEditingProjectId(null)}
                        onSaved={(updated) => {
                            data.setProjects((prev) => prev.map((p) =>
                                p.id === editingProjectId
                                    ? { ...p, name: updated.name, description: updated.description, files: updated.files }
                                    : p,
                            ));
                        }}
                    />
                );
            })()}
        </>
    );
}
